import { useEffect, useMemo, useState } from "react";
import { Button, FormField } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { Select } from "@/components/ui/Select";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/api/client";
import { adminWorkTaskTagsApi, type TaskTagAdminDto } from "@/api/workTasks";

interface Props {
  open: boolean;
  source: TaskTagAdminDto | null;
  candidates: TaskTagAdminDto[];
  onClose: () => void;
  onMerged: () => void;
}

/**
 * Tasks v1.1 § 2.6 — merge dialog. Source is pre-selected from the row
 * action; the target dropdown excludes the source and any archived
 * tags. The preview line is built from the source's
 * <c>totalUsageCount</c> so admins see the blast radius before
 * committing.
 */
export function TagMergeDialog({
  open, source, candidates, onClose, onMerged,
}: Props) {
  const toast = useToast();
  const [targetId, setTargetId] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setTargetId("");
    setError(null);
  }, [open, source?.id]);

  const targets = useMemo(
    () => candidates.filter((c) => !c.isArchived && c.id !== source?.id),
    [candidates, source?.id],
  );

  const target = targets.find((t) => t.id === targetId) ?? null;

  const submit = async () => {
    if (!source || !target) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await adminWorkTaskTagsApi.merge(source.id, target.id);
      toast.success(
        `Merged: ${result.reassignedCount} reassigned, ${result.deduplicatedCount} deduplicated.`,
      );
      onMerged();
      onClose();
    } catch (err) {
      const msg =
        err instanceof ApiError ? err.errors?.[0] ?? err.message : "Could not merge.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!source) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title={`Merge '${source.name}' into another tag`}
      description="The source tag will be archived and its assignments reassigned."
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button
            onClick={submit}
            loading={submitting}
            type="button"
            disabled={!targetId}
          >
            Merge
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-space-3">
        <FormField label="Source" htmlFor="merge-source">
          <input
            id="merge-source"
            readOnly
            value={source.name}
            aria-label="Source tag"
            className="w-full h-10 px-space-3 py-[10px] bg-white-warm border border-border-light rounded-md text-small text-onyx"
          />
        </FormField>

        <FormField label="Target" required htmlFor="merge-target">
          <Select
            id="merge-target"
            value={targetId}
            onChange={(e) => setTargetId(e.target.value)}
          >
            <option value="">Pick a target tag…</option>
            {targets.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </Select>
        </FormField>

        {target && (
          <div
            role="status"
            className="rounded-md bg-white-warm border border-border-light p-space-3 text-small text-onyx"
          >
            Reassign {source.totalUsageCount} task
            {source.totalUsageCount === 1 ? "" : "s"} from{" "}
            <strong>'{source.name}'</strong> to <strong>'{target.name}'</strong> and archive{" "}
            <strong>'{source.name}'</strong>?
          </div>
        )}

        {error && (
          <p role="alert" className="text-label text-status-error">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
